import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";

import claimApi from "../../api/claim";
import { Loader, toast } from "../../common/components/core";
import { FormContainer, FormFooter } from "../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { BesuClaimState } from "../../types/claim";
import { BesuProjectState } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { shortenUUID } from "../../utils/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../utils/errorHandling";
import ProjectContext from "../project/ProjectContext";

import ClaimContext from "./ClaimContext";
import ClaimForm, { ClaimFormValues } from "./components/ClaimForm";
import ClaimPreview from "./components/ClaimPreview";

const EditClaimView = (): JSX.Element => {
    document.title = PageTitle.EditClaimView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const {
        data: claim,
        mutate: mutateClaim,
    } = useContext(ClaimContext);
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (formValues: ClaimFormValues) => {
        setSubmitting(true);
        try {
            const result = await claimApi.resubmit(claim.referenceId, formValues);
            mutateClaim( result.stateAndRefMap.BesuClaimState[0].state.data)

            toast(`Claim #${shortenUUID(claim.referenceId)} submitted`);
            history.push(
                `/project/${project.referenceId}/claim/${claim.referenceId}`,
            );
        } catch (e) {
            setSubmitting(false);
            composeErrorHandlers(
                onStaleStateError(() => {
                    mutateClaim();
                    history.push(
                        `/project/${project.referenceId}/claim/${claim.referenceId}`,
                    );
                    toast("Page has been updated by another user.");
                }),
                onAuthError,
            )(e as GenericError);
        }
    };

    return (
        <PureEditClaimView
            project={project}
            claim={claim}
            isLoading={isSubmitting}
            onSubmit={submit}
            onCancel={() =>
                history.push(
                    `/project/${project.referenceId}/claim/${claim.referenceId}`,
                )
            }
        />
    );
};

type PureEditClaimViewProps = {
    project: BesuProjectState;
    claim: BesuClaimState;
    isLoading: boolean;
    onSubmit: (values: ClaimFormValues) => void;
    onCancel: () => void;
};

export const PureEditClaimView = ({
    project,
    claim,
    isLoading,
    onCancel,
    onSubmit,
}: PureEditClaimViewProps): JSX.Element => {
    const [isPreview, setIsPreview] = useState(false);
    const [formValues, setFormValues] = useState<ClaimFormValues>({
        createdAt: claim.createdAt,
        description: claim.descriptionRef,
        // TODO
        // files: [],
        files: claim.documents.map((doc) => ({
            name: doc.documentName,
            id: doc.documentId,
        })),
    });

    useEffect(() => {
        if (!isLoading) {
            setIsPreview(false);
        }
    }, [isLoading]);

    const preview = (values: ClaimFormValues) => {
        setFormValues(values);
        setIsPreview(true);
    };

    return (
        <Loader overlay isLoading={isLoading} bgClassName="bg-body">
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs="span">
                    <NavLink to={`/project/${project.referenceId}`}>
                        {project.name}
                    </NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    Claim {shortenUUID(claim.referenceId)}
                </Breadcrumb.Item>
            </Breadcrumb>
            {isPreview ? (
                <>
                    <h1 className="mb-5">Preview</h1>
                    <ClaimPreview
                        formValues={formValues}
                        claimApprovers={project.claimApprovers}
                    />
                    <FormFooter className="d-flex justify-content-between">
                        <Button
                            variant="light.tertiary"
                            onClick={() => setIsPreview(false)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={() => onSubmit(formValues)}
                        >
                            Submit
                        </Button>
                    </FormFooter>
                </>
            ) : (
                <FormContainer>
                    <h1 className="mb-3 rem-1-5">Edit Details for Your Claim Submission</h1>
                    <ClaimForm
                        values={formValues}
                        onCancel={onCancel}
                        onSubmit={preview}
                    />
                </FormContainer>
            )}
        </Loader>
    );
};

export default EditClaimView;
